<template>
  <Transition name="slide-from-right">
    <aside ref="asideElement" v-if="openSidebar" id="devis-sidebar">
      <SearchUser v-if="isShopManager" v-show="!sending && !created" v-on:user-found="populateUserData"/>
      <div v-if="sending">
        Votre devis est en cours de création, merci de patienter quelques instants...
        <SvgSpinner/>
      </div>
      <div class="devis__created" v-show="created && !sending"><!-- Keep v-show and not v-if for the animation -->
        <AnimatedCheckMark :done="created"/>
        <p>Votre devis <b>#{{ orderId }}</b> a bien été crée. Vous le recevrez à l'adresse
          <em>{{ email }}</em> d'ici quelques secondes.</p>
        <p>Vous pouvez recevoir autant de devis que vous le souhaitez.</p>
        <div>
          <button type="button" @click="close">Fermer</button>
        </div>
      </div>
      <div class="devis__failed" v-if="failed && !sending">
        <div class="failed__grid">
          <Warning fill="indianred"/>
          <div>
            <p>Une erreur a empêché le devis d'être créé, nous sommes désolés.
            </p>
            <p>Veuillez réessayer ou nous contacter par téléphone.</p>
          </div>
        </div>
        <div>
          <button type="button" @click="close">Fermer</button>
        </div>
      </div>
      <div class="devis">
        <div>
          <div v-show="!sending && !created && !failed">
            <div class="devis__header">
              <h3>Vos coordonnées</h3>
              <div>
          <span v-if="!isLoggedIn">Vous avez un compte go2roues ? <br><a
            href="/mon-compte/">Connectez-vous &#x2192;</a></span>
              </div>
            </div>
            <form ref="form">
              <div class="ppd__civilite">
                <select v-model="civilite">
                  <option value="Monsieur">Mr</option>
                  <option value="Madame">Mme</option>
                  <option value="Mademoiselle">Mlle</option>
                </select>
                <input type="text" v-model.trim="firstName" placeholder="Prénom..." required>
                <input type="text" v-model.trim="lastName" placeholder="Nom..." required>
              </div>
              <input type="text" v-model="company" placeholder="Nom de l'entreprise... (facultatif)">
              <div class="ppd__row">
                <input type="text" v-model="postCode" placeholder="Code Postal..." maxlength="5" pattern="[0-9]{5}"
                       required>
                <input type="tel" v-model="phone" placeholder="Téléphone..." minlength="10" maxlength="10"
                       pattern="^(?:0|\(?\+33\)?\s?|0033\s?)[1-79](?:[\.\-\s]?\d\d){4}$" required>
              </div>
              <input type="email" v-model="email" required placeholder="Email...">
            </form>
          </div>
        </div>
        <div v-show="!sending && !created && !failed">
          <fieldset v-if="Object.keys(options).length > 0">
            <legend>Options</legend>
            <template v-for="(option,id) in options">
              <label :for="`option-${id}`">
                <input :id="`option-${id}`" type="checkbox" :value="id"
                       v-model.number="selectedOptions">{{ sanitize(option.name) }} ({{ option.price }} €)</label>
            </template>
          </fieldset>
          <div v-for="(products,type) in compatibles" v-if="products.length > 0" :class="`devis__${type}`">
            <fieldset>
              <legend>{{ relatedTypes[type] }}</legend>
              <template v-for="product in products">
                <ProductPageDevisRow :product="product" :type="type" @add-product="addProduct"
                                     @remove-product="removeProduct"/>
              </template>
            </fieldset>
          </div>
          <div>
            <fieldset class="devis__accessoires">
              <legend>Accessoires & Équipements divers :</legend>
              <template v-for="access in sortedAccessories">
                <ProductPageDevisRow :product="access" type="misc" @add-product="addProduct"
                                     @remove-product="removeProduct"/>
              </template>
            </fieldset>
          </div>
          <div v-if="!isVO">
            <fieldset>
              <legend>Services</legend>
              <template v-for="service in filteredServices">
                <ProductPageDevisRow :product="service" type="service" @add-product="addProduct"
                                     @remove-product="removeProduct"/>
              </template>
            </fieldset>
          </div>
          <div class="devis__shipping">
            <h4>Méthode de Livraison</h4>
            <label for="shipping-pickup"><input id="shipping-pickup" type="radio" name="shipping" v-model="shipping"
                                                value="agence"/>Retrait en agence
              <MapPin :size="18 "/>
            </label>
            <label for="shipping-delivery"><input id="shipping-delivery" type="radio" name="shipping" v-model="shipping"
                                                  value="domicile"/>Livraison à domicile
              <Truck :size="18"/>
            </label>
          </div>
          <footer>
            <div>
              <button type="button" @click="reset" v-if="isShopManager">
                <Reset :size="14" color="white"/>
              </button>
            </div>
            <div>
              <button type="button" @click="send" class="button__devis">RECEVOIR MON DEVIS</button>
            </div>
          </footer>
        </div>
      </div>
    </aside>
  </Transition>
</template>

<script>
import SearchUser from "@/components/partials/SearchUser";
import Reset from "@/components/icons/Reset";
import SvgSpinner from "@/components/partials/SvgSpinner";
import AnimatedCheckMark from "@/components/partials/AnimatedCheckMark";
import Warning from "@/components/icons/Warning";
import {mapGetters} from "vuex";
import {onKeyUp} from "@vueuse/core";
import Truck from "@/components/icons/Truck.vue";
import MapPin from "@/components/icons/MapPin.vue";
import ProductPageDevisRow from "@/components/features/ProductPageDevisRow.vue";

export default {
  name: "ProductPageDevis",
  components: {Warning, AnimatedCheckMark, SvgSpinner, Reset, SearchUser, Truck, MapPin, ProductPageDevisRow},
  data() {
    return {
      openSidebar: false,
      civilite: 'Monsieur',
      firstName: '',
      lastName: '',
      email: '',
      postCode: '',
      phone: '',
      company: '',
      accessories: [],
      options: [],
      compatibles: {},
      packs: {},
      services: [],
      selectedCrossSells: [],
      selectedOptions: [],
      selectedPacks: [],
      productId: parseInt(g2rObj.postId),
      variationId: '',
      shipping: 'agence',
      sending: false,
      created: false,
      failed: false,
      orderId: '',
      swipeX: 0,
      // step: 1,
    }
  },
  computed: {
    postData() {
      return {
        productId: this.productId,
        variationId: this.variationId,
        civilite: this.civilite,
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        postCode: this.postCode,
        phone: this.phone,
        company: this.company,
        selectedCrossSells: this.selectedCrossSells,
        selectedOptions: this.selectedOptions,
        selectedPacks: this.selectedPacks,
        shipping: this.shipping,
      }
    },
    isVO() {
      return document.body.classList.contains('occasion')
    },
    comptabibleSeparation() {
      return ['chargeur', 'support', 'top_case', 'tablier']
    },
    isLoggedIn() {
      return g2rObj.hasOwnProperty('userId')
    },
    isShopManager() {
      if (!this.isLoggedIn) {
        return false
      }
      return g2rObj.userEmail.indexOf('@go2roues.com') > -1 && g2rObj.userData.canManageWoo;
    },
    filteredServices() {
      if (['91', '92', '93', '94', '95', '75', '77', '78'].includes(this.postCode.substring(0, 2))) {
        return this.services.sort((a, b) => a.price < b.price ? -1 : 1)
      }
      return this.services.filter(service => service.id === 146380)
    },
    sortedAccessories() {
      return this.accessories.sort((a, b) => a.name > b.name ? 1 : -1)
    },
    ...mapGetters(['relatedTypes']),
  },
  watch: {
    openSidebar(value) {
      if (value) {
        this.disableScroll()
        setTimeout(() => {
          document.body.addEventListener('click', this.bodyListener)
          document.addEventListener('touchstart', this.listenTouchStart)
          document.addEventListener('touchmove', this.listenTouchMove)
        }, 200)
      } else {
        document.body.removeEventListener('click', this.bodyListener)
        document.removeEventListener('touchstart', this.listenTouchStart)
        document.removeEventListener('touchmove', this.listenTouchMove)
        this.enableScroll()
        this.selectedCrossSells = []
      }
    },
  },
  methods: {
    addProduct(id) {
      this.selectedCrossSells.push(id)
    },
    removeProduct(id) {
      this.selectedCrossSells = this.selectedCrossSells.filter(item => item !== id)
    },
    listenTouchStart(e) {
      this.swipeX = e.touches[0].clientX
    },
    listenTouchMove(e) {
      let x = e.touches[0].clientX
      if (x - this.swipeX > 100) {
        this.openSidebar = false
      }
    },
    disableScroll() {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      // if any scroll is attempted, set this to the previous value
      window.onscroll = function (e) {
        window.scrollTo({top: scrollTop, behavior: 'smooth'});
      };
    },
    enableScroll() {
      window.onscroll = function () {
      };
    },
    bodyListener(e) {
      if (e.target.id !== 'recevoirundevis' && !this.hasDevisSidebarParent(e.target)) {
        this.openSidebar = false
      }
    },
    openDevisModal() {
      this.openSidebar = true
    },
    populateUserData(user) {
      this.firstName = user.first_name
      this.lastName = user.last_name
      this.company = user.billing_company
      this.email = user.user_email
      this.phone = user.billing_phone
      this.postCode = user.billing_postcode
      this.civilite = user.civilite === '' ? 'Monsieur' : user.civilite
    },
    reset() {
      this.firstName = ''
      this.lastName = ''
      this.email = ''
      this.postCode = ''
      this.phone = ''
      this.company = ''
      this.civilite = 'Monsieur'
      this.selectedCrossSells = []
    },
    setVariationId(variation) {
      this.variationId = variation.variation_id
    },
    send() {
      if (this.$refs.form.checkValidity()) {
        // Save form data to prefill other potential devis
        if (!this.isLoggedIn) {
          sessionStorage.setItem('devis', JSON.stringify(this.$data))
        }
        this.sending = true;
        let postData = {...this.postData}
        // noinspection CssInvalidHtmlTagReference
        const loaAvailable = document.querySelector('loa-sliders') !== null
        if (loaAvailable) {
          const duree = document.querySelector('[name="loa-duree"]').value
          const kmAn = document.querySelector('[name="loa-km-an"]').value
          const mensualite = document.querySelector('[name="loa-mensualite"]').value
          const vr = document.querySelector('[name="loa-vr"]').value
          postData = {...postData, loa: {duree, kmAn, mensualite, vr}}
        }
        $api.post(`g2r/v1/devis`, postData)
          .then(response => response.data)
          .then(data => {
            console.log(data)
            if (!isNaN(data)) {
              this.created = true
              this.orderId = data
              // If the user landed on the page from chatbot, send the lead to targetfirst
              const urlParams = new URLSearchParams(window.location.search);
              if (urlParams.has('devis')) {
                if (typeof watcheeSetLead === 'function') {
                  watcheeSetLead(`${this.firstName} ${this.lastName}`, this.email, document.querySelector('h1.product_title').textContent);
                }
              }
            } else {
              this.failed = true
            }
          })
          .finally(() => this.sending = false)
      } else {
        this.$refs.form.reportValidity()
      }
    },
    close() {
      this.openSidebar = false
      this.created = false
      this.failed = false
      this.selectedCrossSells = []
    },
    sanitize(string) {
      return string.replace('&amp;', '&')
    },
    hasDevisSidebarParent(element) {
      if (!element) {
        // If we reach the root of the document without finding #devis-sidebar, return false
        return false;
      }
      if (element.id === 'devis-sidebar') {
        // Found #devis-sidebar as an ancestor, return true
        return true;
      }
      // Recursively check the parent of the current element
      return this.hasDevisSidebarParent(element.parentElement);
    },
  },
  beforeMount() {
    this.accessories = JSON.parse(document.getElementById('product-accessoires').innerHTML)
    this.options = JSON.parse(document.getElementById('product-options').innerHTML)
    this.compatibles = JSON.parse(document.getElementById('product-compatibles').innerHTML)
    this.services = JSON.parse(document.getElementById('product-services').innerHTML)
    // Prefill if user is not logged in but already sent himself a devis
    if (!this.isLoggedIn && sessionStorage.hasOwnProperty('devis')) {
      const data = JSON.parse(sessionStorage.getItem('devis'))
      const array = ['firstName', 'lastName', 'email', 'postCode', 'phone', 'company', 'civilite']
      array.forEach(key => this[key] = data[key])
    }
  },
  mounted() {
    if (this.isLoggedIn && !this.isShopManager) {
      const userDetails = JSON.parse(document.getElementById('user-details').innerHTML)
      for (const prop in userDetails) {
        this[prop] = userDetails[prop]
      }
    }
    onKeyUp('Escape', () => this.openSidebar = false)
    // Get query param to see if devis=true
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('devis')) {
      this.openSidebar = true
    }
  },
}
</script>

<style scoped lang="scss">
// Transition
$timing: .2s;
.slide-from-right-enter-active {
  animation: slide-from-right-base-opacity-fade $timing;
}

.slide-from-right-leave-active {
  animation: slide-from-right-base-opacity-fade reverse $timing;
}

@keyframes slide-from-right-base-opacity-fade {
  0% {
    transform: translateX(90vw);
  }
  100% {
    transform: translateX(0);
  }
}

aside {
  position: fixed;
  top: 0;
  right: 0;
  background-color: #fcfdff;
  padding: 2rem;
  height: 100%;
  width: 520px;
  max-width: 100vw;
  border-left: 2px solid var(--dark-blue);
  z-index: 100;
  overflow-y: scroll;

  input[type="checkbox"] {
    accent-color: var(--orange);
  }

  input[type="radio"] {
    accent-color: var(--dark-blue);
  }

  .devis__shipping {
    margin: 1rem 0;

    h4 {
      background: linear-gradient(90deg, #ff5b5b, orange);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      margin-top: 2rem;
      margin-bottom: 1rem;
    }

    label {
      cursor: pointer;
      margin-left: 1rem;
      display: flex;
      align-items: center;

      input {
        margin: 0 .5rem 0 0;
      }

      svg {
        margin-left: .5rem;
      }
    }
  }

  footer {
    button {
      margin-top: 1rem;
    }
  }
}

.devis {
  display: grid;
  grid-column-gap: 2rem;
  grid-row-gap: 1rem;
}

.devis__accessoires { // fieldset
  max-height: 300px;
  overflow-y: scroll;
}

.devis__header {
  h3 {
    background: linear-gradient(90deg, #ff5b5b, orange);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  div {
    &:nth-child(2) {
      margin-bottom: 2rem;
    }
  }
}

.devis__created, .devis__failed {
  text-align: center;

  .failed__grid {
    display: grid;
    grid-template-columns: 1fr 5fr;
    margin-bottom: 3rem;
    border: 1px solid indianred;
    padding: 1rem;
    color: indianred;

    p {
      margin-bottom: .5rem;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

form {
  div {
    display: grid;
    grid-column-gap: 1rem;
  }
}

fieldset {
  margin-bottom: 1rem;

  legend {
    padding: 0 .5rem;
    background: linear-gradient(to left, var(--green), var(--dark-blue));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-family: "Saira Semi Condensed", sans-serif;
    font-size: 1.8rem;
  }

  label {
    display: block;
    cursor: pointer;

    [type="checkbox"] {
      margin-right: .5rem;
      margin-bottom: 2px;
      vertical-align: middle;
    }
  }

  &.packs {
    > div {
      margin-left: 2rem;
    }

    select {
      height: 30px;
      padding: 0 1rem;
      width: auto;
    }
  }
}


@media screen and (min-width: 768px) {
  .devis__header {
    display: grid;
    grid-template-columns: 1fr 1fr;

    div {
      &:nth-child(2) {
        line-height: 1;
        font-size: small;
        text-align: right;
      }
    }
  }
}


@media screen and (min-width: 1224px) {
  form {
    div {
      &.ppd__civilite {
        grid-template-columns: 1fr 2.5fr 2.5fr;
      }

      &.ppd__row {
        grid-template-columns: 1fr 1fr;
      }
    }
  }
}
</style>
